import { Code, ConnectError } from '@connectrpc/connect';
import { getTranslator } from '@foir/i18n';
import { msg } from '@foir/i18n';
import { userFacingReason } from '@foir/rpc-web/errors';
import { hashKey, MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Translatable } from '@foir/i18n';

/**
 * Derive a readable message from a failed mutation.
 *
 * This is the fallback for mutations that declare no `onError`, so it cannot
 * know what was being attempted and has only the reason to show. It used to end
 * `error.message` for a non-Connect error, which is a client-side fault naming
 * our own internals — a thrown TypeError read to the user. `userFacingReason`
 * returns null for those and for the codes whose text is written for logs,
 * which is what this generic sentence is for.
 */
function describeMutationError(error: unknown): Translatable {
  return userFacingReason(error) || msg('common.something_went_wrong', 'Something went wrong');
}

/**
 * Errors a retry cannot fix.
 *
 * react-query retries three times by default, which is right for a dropped
 * connection and wrong for an answer. A refusal is an ANSWER: the server has
 * decided, and asking again with the same credentials gets the same decision.
 *
 * The cost is not just wasted calls — it is that the screen keeps saying
 * "loading" throughout. `error` stays null until the retries are exhausted, so
 * a permission denial spent about seven seconds rendering as a skeleton before
 * the user was told anything. Measured on the People page, where a refused
 * user read looked indistinguishable from a page that never loads.
 */
const TERMINAL_CODES = new Set<Code>([
  Code.PermissionDenied,
  Code.Unauthenticated,
  Code.InvalidArgument,
  Code.NotFound,
  Code.AlreadyExists,
  Code.FailedPrecondition,
  Code.Unimplemented,
]);

/**
 * Retry transport failures, never decisions. Non-Connect errors keep the
 * default budget: they are the ones that really are transient.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ConnectError && TERMINAL_CODES.has(error.code)) {
    return false;
  }
  return failureCount < 3;
}

/**
 * The console's QueryClient, as one definition.
 *
 * A global mutation-error fallback toasts when a failed mutation doesn't own its
 * error UX — most mutations have no local onError, so failures used to vanish
 * silently (spinner stops, nothing saved, no feedback). A local `onError`
 * handler, or `meta.suppressGlobalError`, opts out.
 *
 * # Why a factory as well as the singleton
 *
 * `apps/console` and `apps/auth` import the singleton directly. `apps/platform`
 * and `apps/staff` build theirs inside a `getContext()` that the router calls,
 * so they cannot; they each grew their own `new QueryClient()` instead, and with
 * it their own answer to "what happens when a mutation fails". Staff's answer
 * was nothing at all — a bare client, so a mutation without a local `onError`
 * failed in silence — and platform's retry policy knew about one terminal code
 * where this one knows about seven.
 *
 * Both now call this, so the fallback toast and the retry policy are the same
 * four ways rather than three.
 */
export interface ConsoleQueryClientOptions {
  /**
   * The scope the cache is PARTITIONED by, read fresh on every cache lookup.
   *
   * A console whose scope lives in the URL needs this, and the reason is that a
   * query key describes WHAT was asked for and never WHERE. `['operations',
   * 'list']` is the same string in every project, so react-query treats project
   * A's answer as the answer for project B and serves it from cache — the
   * screen shows another project's rows, which is exactly what it looks like.
   *
   * Scoping the KEYS instead would work and is what the call sites should
   * ideally do, but there are ~376 of them and one missed key is a silent
   * cross-project read. Partitioning the cache is one place, covers every key
   * that exists or ever will, and cannot be forgotten by a new call site.
   *
   * Return the same string for the same scope and a DIFFERENT string for a
   * different one; '' is fine for "no scope" (the selection screens). Read it
   * from the same source the request headers use, or the cache and the wire can
   * disagree about which project a row belongs to.
   *
   * Omit entirely and the cache behaves exactly as it did — apps whose scope
   * lives in the session already clear the cache when the session changes.
   */
  scopeKey?: () => string;
}

export function createConsoleQueryClient(options: ConsoleQueryClientOptions = {}): QueryClient {
  const { scopeKey } = options;
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.onError) return;
        if (mutation.meta?.suppressGlobalError === true) return;
        toast.error(getTranslator()(describeMutationError(error)));
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: shouldRetryQuery,
        // hashKey, not JSON.stringify: react-query's own hash sorts object keys,
        // so `{ first: 20, after: null }` and `{ after: null, first: 20 }` are one
        // entry. Prefixing it keeps that and only adds the axis it was missing.
        //
        // THE COMPOSITION IS READ BACK. `@foir/auth`'s `scope-cache.ts` tells
        // one partition from another off this string, because `queryHash` is
        // the only place a cache entry still carries its scope — so the space
        // between the two halves is part of the contract, not a formatting
        // choice. apps/console's `query-client.test.ts` pins the pair together.
        ...(scopeKey ? { queryKeyHashFn: (key) => `${scopeKey()} ${hashKey(key)}` } : {}),
      },
    },
  });
}

/** The shared instance, for apps that mount a client at module scope. */
export const queryClient = createConsoleQueryClient();
